import React from 'react';
import { CodeMirrorCell } from './codemirror';

export interface NotebookCell {
  id: string;
  type: 'code' | 'markdown' | 'raw';
  source: string;
  outputs?: any[];
  execution_count?: number | null;
  metadata?: Record<string, any>;
}

interface NotebookEditorProps {
  cell: NotebookCell;
  isActive: boolean;
  isExecuting: boolean;
  onExecute: (code: string) => Promise<void>;
  onChange: (code: string) => void;
  onFocus: () => void;
  onDelete: () => void;
  onAddCellAbove: () => void;
  onAddCellBelow: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onChangeCellType: (type: 'code' | 'markdown' | 'raw') => void;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  sessionId?: string;
  onApplyFix?: (code: string) => void;
  onInsertCellAbove?: (code: string) => void;
  onClearOutput?: () => void;
  executionTimeMs?: number;
  requestComplete?: (code: string, cursorPos: number) => Promise<{ matches: string[]; cursor_start: number; cursor_end: number } | null>;
  requestInspect?: (code: string, cursorPos: number) => Promise<{ found: boolean; data: Record<string, string> } | null>;
  onRunAbove?: () => void;
  onRunBelow?: () => void;
  onSplitCell?: (cursorPos: number) => void;
  isQueued?: boolean;
  onUpdateTags?: (tags: string[]) => void;
  proxyAuthCode?: string | null;
  getProxyAuthCode?: () => Promise<string | null>;
  onApplyCodeSuggestion?: (code: string) => void;
  shouldFocusEditor?: boolean;
  onFocusHandled?: () => void;
}

/**
 * Notebook cell editor using CodeMirror 6.
 * This component wraps CodeMirrorCell to maintain compatibility with CellManager.
 */
export const NotebookEditor = React.memo<NotebookEditorProps>(({
  cell,
  isActive,
  isExecuting,
  onExecute,
  onChange,
  onFocus,
  onDelete,
  onAddCellAbove,
  onAddCellBelow,
  onMoveUp,
  onMoveDown,
  onChangeCellType,
  onNavigateUp,
  onNavigateDown,
  sessionId,
  onApplyFix,
  onInsertCellAbove,
  onClearOutput,
  executionTimeMs,
  requestComplete,
  requestInspect,
  onRunAbove,
  onRunBelow,
  onSplitCell,
  isQueued,
  onUpdateTags,
  proxyAuthCode,
  getProxyAuthCode,
  onApplyCodeSuggestion,
  shouldFocusEditor,
  onFocusHandled,
}) => {
  // Handle execute with async wrapper
  const handleExecute = async (code: string) => {
    await onExecute(code);
  };

  return (
    <CodeMirrorCell
      cell={cell}
      isActive={isActive}
      isExecuting={isExecuting}
      onExecute={handleExecute}
      onChange={onChange}
      onFocus={onFocus}
      onDelete={onDelete}
      onAddCellAbove={onAddCellAbove}
      onAddCellBelow={onAddCellBelow}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      onChangeCellType={onChangeCellType}
      onNavigateUp={onNavigateUp}
      onNavigateDown={onNavigateDown}
      sessionId={sessionId}
      onApplyFix={onApplyFix}
      onInsertCellAbove={onInsertCellAbove}
      onClearOutput={onClearOutput}
      executionTimeMs={executionTimeMs}
      requestComplete={requestComplete}
      requestInspect={requestInspect}
      onRunAbove={onRunAbove}
      onRunBelow={onRunBelow}
      onSplitCell={onSplitCell}
      isQueued={isQueued}
      onUpdateTags={onUpdateTags}
      proxyAuthCode={proxyAuthCode}
      getProxyAuthCode={getProxyAuthCode}
      onApplyCodeSuggestion={onApplyCodeSuggestion}
      shouldFocusEditor={shouldFocusEditor}
      onFocusHandled={onFocusHandled}
    />
  );
}, (prev, next) => (
  prev.cell.id === next.cell.id &&
  prev.cell.source === next.cell.source &&
  prev.cell.type === next.cell.type &&
  prev.cell.outputs === next.cell.outputs &&
  prev.cell.execution_count === next.cell.execution_count &&
  prev.isActive === next.isActive &&
  prev.isExecuting === next.isExecuting &&
  prev.isQueued === next.isQueued &&
  prev.sessionId === next.sessionId &&
  prev.executionTimeMs === next.executionTimeMs &&
  prev.proxyAuthCode === next.proxyAuthCode &&
  prev.shouldFocusEditor === next.shouldFocusEditor
));